import * as THREE from 'three';
import { SVGLoader } from 'three/addons/loaders/SVGLoader.js';
import { physicsWorld, getRapier } from './physics.js';
import { config } from './config.js';
import { rigidBodies, svgObjects } from './objects-shared.js';

async function createSVGData(svgText, placement) {
    const svgLoader = new SVGLoader();
    const svgData = svgLoader.parse(svgText);
    
    const geometries = [];
    const materials = [];

    svgData.paths.forEach((path) => {
        const fillColor = path.userData.style.fill;
        const material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(fillColor),
            metalness: 0.2,
            roughness: 0.1,
            emissive: placement.neonColor,
            emissiveIntensity: config.svg.emissiveIntensityNormal
        });
        materials.push(material);

        const shapes = SVGLoader.createShapes(path);
        const geometry = new THREE.ExtrudeGeometry(shapes, {
            depth: placement.depth,
            bevelEnabled: false
        });
        geometries.push(geometry);
    });
    
    return { geometries, materials };
}

function createSVGMesh(scene, svgData, placement, RAPIER) {
    const svgGroup = new THREE.Group();
    const materials = svgData.materials.map(m => m.clone());

    svgData.geometries.forEach((geometry, index) => {
        const mesh = new THREE.Mesh(geometry, materials[index]);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        svgGroup.add(mesh);
    });

    const tempBox = new THREE.Box3().setFromObject(svgGroup);
    const size = tempBox.getSize(new THREE.Vector3());
    const center = tempBox.getCenter(new THREE.Vector3());
    
    svgGroup.children.forEach(child => {
        child.position.sub(center);
    });

    svgGroup.scale.setScalar(placement.scale);
    svgGroup.rotation.set(placement.rot[0], placement.rot[1], placement.rot[2]);
    svgGroup.position.set(placement.pos[0], placement.pos[1], placement.pos[2]);
    scene.add(svgGroup);

    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(svgGroup.rotation);

    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(placement.pos[0], placement.pos[1], placement.pos[2])
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.7)
        .setAngularDamping(0.7)
        .setCanSleep(false);
    
    const body = physicsWorld.createRigidBody(bodyDesc);
    body.userData = {
    type: 'svg',
    meshReference: svgGroup
};
    
    const scaledSize = size.clone().multiplyScalar(placement.scale);
    
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
        scaledSize.x / 2,
        scaledSize.y / 2,
        scaledSize.z / 2
    )
        .setRestitution(config.physics.svgRestitution)
        .setFriction(config.physics.svgFriction)
        .setDensity(1.0)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    
    physicsWorld.createCollider(colliderDesc, body);
    
    svgGroup.userData.physicsBody = body;
    svgGroup.userData.isSVG = true;
    svgGroup.userData.materials = materials;
    svgGroup.userData.neonColor = placement.neonColor;
    svgGroup.userData.hitTime = 0;
    svgGroup.userData.isHit = false;
    
    rigidBodies.push(svgGroup);
    svgObjects.push(svgGroup);
}

function createSVGArray(scene, sharedSVGData, basePlacement, RAPIER) {
    const count = config.svg.arrayCount;
    const radius = config.svg.arrayRadius;
    const type = config.svg.arrayType;
    
    for (let i = 0; i < count; i++) {
        const newPlacement = { ...basePlacement };
        let newPos;

        if (type === 'circle') {
            const angle = (i / count) * Math.PI * 2;
            newPos = [
                basePlacement.pos[0] + Math.cos(angle) * radius,
                basePlacement.pos[1],
                basePlacement.pos[2] + Math.sin(angle) * radius
            ];
        } else if (type === 'line') {
            newPos = [
                basePlacement.pos[0] + (i - (count - 1) / 2) * (radius / (count -1) * 2),
                basePlacement.pos[1],
                basePlacement.pos[2]
            ];
        } else if (type === 'grid') {
            const gridSize = Math.ceil(Math.sqrt(count));
            const spacing = radius / (gridSize - 1);
            const row = Math.floor(i / gridSize);
            const col = i % gridSize;
            newPos = [
                basePlacement.pos[0] + (col - (gridSize - 1) / 2) * spacing,
                basePlacement.pos[1],
                basePlacement.pos[2] + (row - (gridSize - 1) / 2) * spacing
            ];
        }
        newPlacement.pos = newPos;
        
        createSVGMesh(scene, sharedSVGData, newPlacement, RAPIER);
    }
}

export async function loadAndCreateSVGObjects(scene) {
    const RAPIER = getRapier();
    
    const svgPlacements = [

        { file: 'sny.svg', positions: [{ pos: [0, 30, -50], rot: [Math.PI, 0, 0], scale: 2, depth: 1, neonColor: 0xffffff }] },
        { file: 's9.svg', positions: [{ pos: [100, 42, -60], rot: [Math.PI, 0, 0], scale: 1, depth: 2, neonColor: 0x00ff80 }] },
        { file: 's4.svg', positions: [{ pos: [-16, 64, -600], rot: [Math.PI, 0, 0], scale: 2, depth: 2, neonColor: 0xffffff }] },
        { file: 's8.svg', positions: [{ pos: [-100, 45, -10], rot: [Math.PI, 0, 0], scale: 1, depth: 4, neonColor: 0xff0000 }] },
    ];

    for (const svgSet of svgPlacements) {
        try {
            const response = await fetch(`./img/${svgSet.file}`);
            const svgText = await response.text();
            
            const sharedSVGData = await createSVGData(svgText, svgSet.positions[0]);

            for (const placement of svgSet.positions) {
                createSVGMesh(scene, sharedSVGData, placement, RAPIER);
            }
            
            if (config.svg.arrayEnabled && svgSet.positions.length > 0) {
                createSVGArray(scene, sharedSVGData, svgSet.positions[0], RAPIER);
            }

        } catch (e) {
            console.warn(`Failed to load SVG ${svgSet.file}:`, e);
        }
    }
}

export function triggerSVGHit(svgObject) {
    if (!svgObject.userData.isSVG) return;
    
    svgObject.userData.isHit = true;
    svgObject.userData.hitTime = performance.now();
    
    if (svgObject.userData.materials) {
        svgObject.userData.materials.forEach(mat => {
            mat.emissiveIntensity = config.svg.emissiveIntensityHit;
        });
    }
}

export function updateSVGEmissions() {
    const currentTime = performance.now();
    
    svgObjects.forEach(obj => {
        if (obj.userData.isHit && obj.userData.materials) {
            const elapsed = (currentTime - obj.userData.hitTime) / 1000;
            const fadeDuration = config.svg.hitFadeDuration;
            
            if (elapsed < fadeDuration) {
                const progress = elapsed / fadeDuration;
                const intensity = config.svg.emissiveIntensityHit + 
                    (config.svg.emissiveIntensityNormal - config.svg.emissiveIntensityHit) * progress;
                
                obj.userData.materials.forEach(mat => {
                    mat.emissiveIntensity = intensity;
                });
            } else {
                obj.userData.isHit = false;
                obj.userData.materials.forEach(mat => {
                    mat.emissiveIntensity = config.svg.emissiveIntensityNormal;
                });
            }
        }
    });
}